<script setup>
import { defineProps, defineExpose, ref } from 'vue';
import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import ConteudoModal from './ConteudoModal.vue';
import CartaoOSFilhas from '../CartaoOSFilhas.vue';
import OrdemDeServicoService from '@/api/v2/services/OrdemDeServicoService.js';
import UtilNumber from '@/utils/UtilNumber';

const props = defineProps({
    onClose: { type: Function, default: null }
});

const modalFullscreenRef = ref();
const dado = ref({});
const historico = ref([]);
const itensDaOS = ref([]);
const osFilhas = ref([]);

const pegaId = (value) => {
    if (value && typeof value === 'object') return value.id || value._id || '';
    return value || '';
};

const primeiroValor = (...valores) => {
    for (const valor of valores) {
        if (valor !== undefined && valor !== null && valor !== '') return valor;
    }
    return '';
};

const normalizaItemDaOS = (item = {}, index = 0) => {
    const itemRef = item?.item && typeof item.item === 'object' ? item.item : {};
    const unidadeRef = item?.unidade && typeof item.unidade === 'object' ? item.unidade : {};

    const quantidade = UtilNumber.toNumber(primeiroValor(item?.quantidade, item?.qtd, item?.quantidadeSolicitada, 0));
    const valorUnitario = UtilNumber.toNumber(primeiroValor(item?.valorUnitario, item?.valorUnit, item?.precoUnitario, 0));

    return {
        ...item,
        id: primeiroValor(item?.id, item?._id, `os-item-${index}`),
        _id: primeiroValor(item?._id, item?.id, `os-item-${index}`),
        sequencia: String(primeiroValor(item?.sequencia, item?.sequence, '') || ''),
        itemCodigoExt: String(primeiroValor(item?.itemCodigoExt, item?.itemCodigo, item?.codigoItem, itemRef?.codeExt, itemRef?.code, itemRef?.codigo, '')),
        descricao: String(primeiroValor(item?.descricao, item?.description, itemRef?.description, itemRef?.descricao, '')),
        status: String(primeiroValor(item?.status, item?.situacao, '')),
        destino: String(primeiroValor(item?.destino, item?.destination, '')),
        unidadeSigla: String(primeiroValor(item?.unidadeSigla, item?.siglaUnidade, unidadeRef?.sigla, '')),
        quantidade,
        quantidadeCompra: UtilNumber.toNumber(primeiroValor(item?.quantidadeCompra, item?.qtdComprada, 0)),
        quantidadeParaEntregar: UtilNumber.toNumber(primeiroValor(item?.quantidadeParaEntregar, item?.qtdParaEntregar, 0)),
        quantidadeEntregue: UtilNumber.toNumber(primeiroValor(item?.quantidadeEntregue, item?.qtdEntregue, 0)),
        quantidadeParaEmbarcar: UtilNumber.toNumber(primeiroValor(item?.quantidadeParaEmbarcar, item?.qtdParaEmbarcar, 0)),
        quantidadeEmbarcada: UtilNumber.toNumber(primeiroValor(item?.quantidadeEmbarcada, item?.qtdEmbarcada, 0)),
        pesoTotal: UtilNumber.toNumber(primeiroValor(item?.pesoTotal, item?.peso, 0)),
        valorUnitario,
        valorTotal: UtilNumber.toNumber(primeiroValor(item?.valorTotal, item?.precoTotal, quantidade * valorUnitario, 0)),
        dataSolicitacao: primeiroValor(item?.dataSolicitacao, ''),
        dataNecessidade: primeiroValor(item?.dataNecessidade, item?.dataPrevisao, ''),
        dataEntrega: primeiroValor(item?.dataEntrega, item?.deliveryDate, ''),
        desenhoFabricacao: String(primeiroValor(item?.desenhoFabricacao, '')),
        desenhoMontagem: String(primeiroValor(item?.desenhoMontagem, '')),
        fornecedorDescricao: String(primeiroValor(item?.fornecedorDescricao, item?.fornecedorNomeFantasia, item?.fornecedorNome, item?.fornecedor?.nomeFantasia, item?.fornecedor?.name, item?.fornecedor, '')),
    };
};

const abrirModal = async (dadoParaVisualizar = {}) => {
    try {
        const ordemDeServicoCompletaResponse = await OrdemDeServicoService.pegaOrdemDeServicoCompleta(pegaId(dadoParaVisualizar?.id || dadoParaVisualizar?._id));
        const ordemDeServicoCompleta = ordemDeServicoCompletaResponse?.data || {};

        const itemCorrigido = {
            ...dadoParaVisualizar,
            ...ordemDeServicoCompleta,
            itens: Array.isArray(ordemDeServicoCompleta?.itens) ? ordemDeServicoCompleta.itens : (Array.isArray(dadoParaVisualizar?.itens) ? dadoParaVisualizar.itens : []),
        };

        const itemDaOSId = pegaId(itemCorrigido?.itemOrdemDeServico);

        const [itensDaOSResponse, itemDaOSResponse, osFilhasResponse] = await Promise.allSettled([
            OrdemDeServicoService.pegaItensDaOrdemDeServico(itemCorrigido.id || itemCorrigido._id, itemCorrigido.itens || []),
            OrdemDeServicoService.pegaItemDaOS(itemDaOSId),
            !pegaId(itemCorrigido.osMae) ? OrdemDeServicoService.pegaOsFilhas(itemCorrigido.id || itemCorrigido._id) : Promise.resolve({ status: 200, data: [] })
        ]);

        const itemDaOS = itemDaOSResponse.status === 'fulfilled' ? (itemDaOSResponse.value?.data || {}) : {};
        itensDaOS.value = itensDaOSResponse.status === 'fulfilled' && Array.isArray(itensDaOSResponse.value?.data)
            ? itensDaOSResponse.value.data.map((item = {}, index) => normalizaItemDaOS(item, index))
            : [];

        const idsItensCarregados = Array.isArray(itensDaOS.value)
            ? itensDaOS.value.map((item = {}) => pegaId(item?.id || item?._id)).filter(Boolean)
            : [];

        // Buscar histórico com tratamento de erro
        try {
            const historicoResponse = await OrdemDeServicoService.pegaHistoricoItemDaOS(
                itensDaOS.value,
                pegaId(itemCorrigido.id || itemCorrigido._id)
            );
            historico.value = Array.isArray(historicoResponse?.data) ? historicoResponse.data : [];
        } catch (erroHistorico) {
            console.error('Erro ao buscar histórico, continuando sem ele:', erroHistorico);
            historico.value = [];
        }

        osFilhas.value = osFilhasResponse.status === 'fulfilled' && Array.isArray(osFilhasResponse.value?.data)
            ? osFilhasResponse.value.data
            : [];

        dado.value = {
            ...itemCorrigido,
            itemOrdemDeServicoDetalhe: itemDaOS,
        };

        await modalFullscreenRef.value?.abrirModal();
    } catch (erro) {
        console.error('Erro ao abrir modal de visualização:', erro);
    }
};

const fecharModal = async () => {
    await modalFullscreenRef.value?.fecharModal();
};

const aoFecharModal = async () => {
    await props.onClose?.();
};

defineExpose({ abrirModal, fecharModal });
</script>

<template>
    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        :disabled="false"
        @onClose="aoFecharModal"
    >
        <template #modal-navegacao-titulo>
            Visualizar Ordem de Serviço
        </template>

        <template #modal-conteudo>
            <div class="c-modal-conteudo">
                <ConteudoModal
                    :dado="dado"
                    :disabled="true"
                    :itensDaOS="itensDaOS"
                    :historico="historico"
                />

                <CartaoOSFilhas :osFilhas="osFilhas" />
            </div>
        </template>
    </ModalFullscreen>
</template>

<style scoped>
.c-modal-conteudo {
    display: grid;
    gap: 8px;
    align-content: start;
}
</style>
